import { makeStyles, Typography } from "@material-ui/core"

import { colors } from "../../../utils/theme"
import {
  ResponseOption,
  SharedCategoriesProps,
} from "../../../utils/safetyPlanUtils"
import Options from "./options/Options"

/**
 * Renders a single category group: an optional header, optional descriptive
 * body text for distraction/support contact categories, and the list of
 * response option controls for that category.
 *
 * Used by both EditSafetyPlan (client-facing modal) and SafetyPlanPractitioner
 * (practitioner-facing inline sections).
 *
 * @param category    - The category group to render.
 * @param onClick     - Callback invoked when the user selects or toggles an option.
 * @param selection   - The current list of selected items.
 * @param onChange    - Callback invoked when free-text content changes.
 * @param skipIfPlace - When true, renders nothing for the "Add a place" category.
 *                      Used by EditSafetyPlan, which moves place entry to the dialog footer.
 */
export default function Categories({
  category,
  onClick,
  selection,
  onChange,
  skipIfPlace = false,
  newSafetyPlanView,
}: SharedCategoriesProps) {
  const classes = useStyles()

  const isPlace = category.category === "Add a place"

  if (skipIfPlace && isPlace) return <></>

  let header = category.category

  // Do not show header for these categories
  const showHeader = ![
    "Custom",
    "Triggers",
    "Add a place",
    "Contact",
    "Yes or No",
    "Distractions",
  ].includes(category.category)

  const options = category.response_options.map(
    (d: ResponseOption, index: number) => (
      <Options
        option={d}
        onClick={onClick}
        type={category.category_type}
        selection={selection}
        onChange={onChange}
        newSafetyPlanView={newSafetyPlanView}
        key={index.toString()}
      />
    )
  )

  return (
    <div className={classes.categoriesContainer} key={category.category_id}>
      {showHeader && (
        <Typography className={classes.categoryName}>{header}</Typography>
      )}
      {!!category.categoryDescription && (
        <Typography
          className={classes.categoryDescriptionTxt}
          dangerouslySetInnerHTML={{
            __html: category?.categoryDescription || "",
          }}
        />
      )}
      {options}
      <div className={classes.divider} />
    </div>
  )
}

const useStyles = makeStyles(() => ({
  categoriesContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    alignSelf: "stretch",
    paddingTop: "10px",
  },
  categoryName: {
    flex: "1 0 0",
    color: colors.neutral.asphalt,
    textAlign: "center",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: "24px",
  },
  divider: {
    height: "2px",
    alignSelf: "stretch",
    background: colors.safetyPlan.grayscale.black10,
  },
  categoryDescriptionTxt: {
    color: colors.neutral.asphalt,
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "20px",
    alignSelf: "stretch",
  },
}))
